import { Box, Text, useColorModeValue, VStack } from '@chakra-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import ScrollableTable from './ScrollableTable';
interface UsageHistoryTabProps {
  usageData: any;
}

const UsageHistoryTab: React.FC<UsageHistoryTabProps> = ({ usageData }) => {
  const { t } = useTranslation();

  const colorText = useColorModeValue('gray.800', 'white');

  const formatDate = (yearMonth: any) => {
    if (!yearMonth) {
      return 'N/A';
    }

    // Handle string format like "2025-08"
    if (typeof yearMonth === 'string') {
      return yearMonth;
    }

    // Handle object format like { year: 2025, month: "AUGUST" }
    if (yearMonth.year && yearMonth.month) {
      return `${yearMonth.year}-${yearMonth.month.toLowerCase().slice(0, 3)}`;
    }

    return 'N/A';
  };

  // Sort data by date (newest first)
  const sortedData = React.useMemo(() => {
    const data = usageData?.monthly_summaries || [];
    return [...data].sort((a, b) => {
      const dateA = formatDate(a.year_month);
      const dateB = formatDate(b.year_month);

      // Convert to comparable format (YYYY-MM)
      const [yearA, monthA] = dateA.split('-').map(Number);
      const [yearB, monthB] = dateB.split('-').map(Number);

      // Sort by year first, then by month (descending)
      if (yearA !== yearB) {
        return yearB - yearA; // Newer year first
      }
      return monthB - monthA; // Newer month first
    });
  }, [usageData]);

  const borderColor = useColorModeValue('0.5px solid rgb(209, 213, 219)', '1px solid');

  return (
    <VStack align='stretch' h='90%' spacing={4}>
      <Box overflow='hidden' h='400px' border={borderColor} borderRadius='md'>
        <ScrollableTable
          columns={[
            {
              title: t('managementSystem.companyDetail.date'),
              dataIndex: 'year_month',
              key: 'year_month',
              width: '120px',
              render: (row: any) => <Text color={colorText}>{formatDate(row.year_month)}</Text>,
            },
            {
              title: t('managementSystem.companyDetail.totalQuestions'),
              dataIndex: 'total_questions',
              key: 'total_questions',
              width: '140px',
              render: (row: any) => <Text color={colorText}>{row.total_questions}</Text>,
            },
            {
              title: t('managementSystem.companyDetail.uploadedData'),
              dataIndex: 'total_storage_gb',
              key: 'total_storage_gb',
              width: '160px',
              render: (row: any) => (
                <Text color={colorText}>
                  {typeof row.total_storage_gb === 'number'
                    ? (() => {
                        const storageInMB = row.total_storage_gb * 1024;
                        return storageInMB % 1 === 0
                          ? `${storageInMB}`
                          : `${storageInMB.toFixed(2).replace(/\.?0+$/, '')}`;
                      })()
                    : row.total_storage_gb}
                </Text>
              ),
            },
            {
              title: t('managementSystem.companyDetail.totalTranslations'),
              dataIndex: 'total_translations',
              key: 'total_translations',
              width: '140px',
              render: (row: any) => <Text color={colorText}>{row.total_translations}</Text>,
            },
          ]}
          data={sortedData}
          rowKey='year_month'
          noDataText={t('managementSystem.companyDetail.noData')}
        />
      </Box>
    </VStack>
  );
};

export default UsageHistoryTab;
